import m from '../m.js'
import { api, auth } from '../api.js'
import { issueHref, linkify } from '../autolinks.js'
import { onDismiss } from '../dismiss.js'
import { drafts } from '../drafts.js'
import { language, timeAgo } from '../i18n.js'
import { icon, stateLabelView } from '../icons.js'
import { keyBindingsView } from '../keyBindings.js'
import { DONE, filterQuery, laneCards, laneOf } from '../lanes.js'
import { linkTask, renderMarkdown, taskLines } from '../markdown.js'
import { settings } from '../settings.js'
import { board, detail } from '../store.js'
import {
  canDeleteBranch,
  canRebase,
  deleteBranchButtonView,
  githubHref,
  mergeButtonView,
  claudeActionsView,
  prActionsView,
  rebaseButtonView,
} from './card.js'
import { diffView } from './diff.js'
import { editorView } from './editor.js'
import { eventRowView, timelineRows } from './timeline.js'
import {
  avatarView,
  htmlView,
  labelView,
  labelDotView,
  pickerView,
} from './widgets.js'

function closeDetail() {
  return m.route.set(`/${board.key}`, filterQuery())
}

const titleView = m.withState(() => {
  let editing = false
  let undismiss = null // see dismiss.js: a click outside saves, Esc cancels
  async function save(item, value) {
    if (!editing) return
    editing = false
    value = value.trim()
    if (!value || value === item.title) return
    item.title = value
    await board.patch(item.number, { title: value }).catch(() => {})
  }
  return ({ item }) =>
    editing
      ? [
          keyBindingsView(
            'title',
            { enter: () => document.activeElement.blur() },
            { skipInInputs: false }
          ),
          m('input.title-input', {
            value: item.title,
            oncreate: ({ dom }) => {
              dom.select()
              undismiss = onDismiss({
                contains: target => target === dom,
                close: () => save(item, dom.value),
                escape: () => (editing = false),
              })
            },
            onremove: () => undismiss?.(),
            onblur: evt => save(item, evt.target.value),
          }),
        ]
      : m.m(
          'h1.title',
          {
            cls: { editable: item.viewerCanUpdate },
            title: item.viewerCanUpdate ? m.t('detail.titleHint') : '',
            // a click on a reference or the GitHub link follows it instead
            onclick: evt => {
              if (!evt.target.closest('a')) editing = item.viewerCanUpdate
            },
          },
          [
            m('span.title-text', linkify(item.title)),
            m('span.muted.number', [
              ` #${item.number}`,
              m(
                'a.gh-ref',
                {
                  href: githubHref(item),
                  target: '_blank',
                  title: m.t('card.openOnGithub'),
                  'aria-label': m.t('card.openOnGithub'),
                },
                icon('mark-github')
              ),
            ]),
          ]
        )
})

// comment ids whose delete button currently asks "Delete?"; kept as data, not component state
const confirmingDelete = new Map() // id → reset timer
// GitHub can still list a comment for a while after deleting it; reloads leave these out
const deletedComments = new Set()

/** Trash button that asks once: the first click turns it into "Delete?", the second deletes. */
function deleteButton(id, ondelete) {
  const confirming = confirmingDelete.has(id)
  return m(
    'button.delete-comment',
    {
      class: confirming ? 'small confirming' : 'icon-button',
      title: confirming ? m.t('detail.deleteConfirm') : m.t('detail.delete'),
      'aria-label': confirming
        ? m.t('detail.deleteConfirm')
        : m.t('detail.delete'),
      onclick: () => {
        clearTimeout(confirmingDelete.get(id))
        if (confirming) {
          confirmingDelete.delete(id)
          return ondelete()
        }
        confirmingDelete.set(
          id,
          setTimeout(() => {
            confirmingDelete.delete(id)
            m.redraw()
          }, 3000)
        )
      },
    },
    [icon('trash'), confirming && m.t('detail.deleteShort')]
  )
}

/** Issue body or a comment as GitHub timeline item: rendered HTML, switching to the editor in place. */
const postView = m.withState(initial => {
  // an edit interrupted by closing the modal or a reload reopens with its draft
  let editing = drafts.get(initial.draftKey) != null
  let askDelete = false // an emptied comment was saved: offer to delete it instead
  return ({
    id,
    author,
    createdAt,
    html,
    body,
    saving,
    failed,
    canEdit,
    onsave,
    ondelete,
    ontask,
    label,
    draftKey,
  }) =>
    m('.post', [
      m('.post-avatar', avatarView({ user: author, size: 40 })),
      m.m('.post-box', { cls: { failed } }, [
        m('.post-head', [
          m('strong', author?.login || 'ghost'),
          m(
            'span.muted',
            { title: new Date(createdAt).toLocaleString(language) },
            m.t(label, { time: timeAgo(createdAt) })
          ),
          failed
            ? m('span.error', m.t('detail.notSaved'))
            : saving && m('span.muted', m.t('detail.saving')),
          m('.spacer'),
          canEdit &&
            !editing &&
            m(
              'button.icon-button',
              {
                title: m.t('detail.edit'),
                'aria-label': m.t('detail.edit'),
                onclick: () => (editing = true),
              },
              icon('pencil')
            ),
          ondelete && !editing && deleteButton(id, ondelete),
        ]),
        editing
          ? editorView({
              value: body,
              draftKey,
              autofocus: true,
              saveLabel: m.t('detail.update'),
              oncancel: () => {
                editing = false
                askDelete = false
              },
              onsave: text => {
                // GitHub has no empty comments; an empty description is fine
                if (!text.trim() && ondelete) {
                  askDelete = true
                  return
                }
                editing = false
                askDelete = false
                onsave(text)
              },
            })
          : html == null && body
            ? m('.markdown-body.raw', body) // until render() has GitHub's HTML
            : htmlView({ html, ontask: canEdit && !saving ? ontask : null }),
        editing &&
          askDelete &&
          m('.ask-delete', [
            icon('trash'),
            m('span', m.t('detail.emptyComment')),
            m('.spacer'),
            m(
              'button.small',
              { onclick: () => (askDelete = false) },
              m.t('detail.keepEditing')
            ),
            m(
              'button.small.danger',
              {
                onclick: () => {
                  editing = false
                  askDelete = false
                  ondelete()
                },
              },
              m.t('detail.delete')
            ),
          ]),
      ]),
    ])
})

/** Sidebar section like GitHub's: heading with a gear menu, value below. A click anywhere on it opens the menu. */
function field(title, picker, content) {
  return m(
    '.field',
    {
      onclick:
        picker &&
        (evt => {
          if (evt.target.closest('a, button, input, .picker')) return
          evt.currentTarget.querySelector('.picker-toggle')?.click()
        }),
    },
    [m('.field-head', [m('h3', title), picker]), m('.field-value', content)]
  )
}

function gear(attrs) {
  return pickerView({
    ...attrs,
    title: attrs.heading,
    className: 'gear',
    toggle: icon('gear'),
  })
}

/** Close/reopen next to "Comment", like on GitHub; merged pull requests have none. */
function stateButton(item) {
  const set = fields => () => board.patch(item.number, fields).catch(() => {})
  const button = (iconName, cls, label, hint, onClick) =>
    m('button', { title: hint, onclick: onClick }, [
      icon(iconName, { class: `state ${cls}` }),
      m.t(label),
    ])
  if (item.type === 'issue') {
    return item.state === 'open'
      ? button(
          'issue-closed',
          'done',
          'detail.closeIssue',
          m.t('detail.closeIssueHint'),
          set({ state: 'closed', state_reason: 'completed' })
        )
      : button(
          'issue-reopened',
          'open',
          'detail.reopenIssue',
          m.t('detail.reopenIssue'),
          set({ state: 'open' })
        )
  }
  if (item.state === 'open')
    return button(
      'git-pull-request-closed',
      'closed',
      'detail.closePr',
      m.t('detail.closePrHint'),
      set({ state: 'closed' })
    )
  if (item.state === 'closed')
    return button(
      'git-pull-request',
      'open',
      'detail.reopenPr',
      m.t('detail.reopenPr'),
      set({ state: 'open' })
    )
  return null
}

function sidebarView({ item, data }) {
  const { repo, config } = data
  // the board card is updated optimistically on moves, the detail only after reloading
  const current = laneOf(board.card(item.number) || item)
  const canEdit = repo.canTriage
  const laneName = name => (name === DONE ? config.done.name : name)
  const laneView = name => {
    const lane = config.lanes.find(l => l.name === name)
    return [
      m.m('span.lane-dot', {
        cls: { done: name === DONE },
        vars: { background: lane?.color && `#${lane.color}` },
      }),
      laneName(name),
    ]
  }
  const assign = assignees =>
    board.patch(item.number, { assignees }).catch(() => {})
  const me = auth.user?.login

  return m('aside.sidebar', [
    field(
      m.t('detail.lane'),
      gear({
        heading: m.t('detail.moveToLane'),
        disabled: !canEdit || (item.type === 'pr' && current === DONE),
        multi: false,
        selected: [current],
        options: [
          ...config.lanes.map(l => l.name),
          ...(item.type === 'issue' ? [DONE] : []),
        ].map(name => ({
          value: name,
          label: laneName(name),
          view: () => laneView(name),
        })),
        onchange: ([lane]) => {
          if (!lane) return
          const first = laneCards(data, lane).find(
            c => c.number !== item.number
          )
          board.move(item.number, lane, first?.number ?? null).catch(() => {})
        },
      }),
      laneView(current)
    ),

    field(
      m.t('detail.assignees'),
      gear({
        heading: m.t('card.assignHeading'),
        disabled: !canEdit,
        selected: item.assignees.map(a => a.login),
        options: data.assignees.map(a => ({
          value: a.login,
          label: a.login,
          view: () => [avatarView({ user: a }), a.login],
        })),
        onchange: assign,
      }),
      item.assignees.length
        ? item.assignees.map(user =>
            m('.person', [avatarView({ user }), user.login])
          )
        : m('.muted', [
            m.t('detail.noOne'),
            canEdit &&
              me && [
                '—',
                m(
                  'button.link',
                  { onclick: () => assign([me]) },
                  m.t('detail.assignYourself')
                ),
              ],
          ])
    ),

    field(
      m.t('detail.labels'),
      gear({
        heading: m.t('card.applyLabels'),
        disabled: !canEdit,
        selected: item.labels.map(l => l.name),
        options: data.labels.map(l => ({
          value: l.name,
          label: l.name,
          view: () => [labelDotView({ color: l.color }), l.name],
        })),
        // lane labels never reach the board; the server keeps them
        onchange: labels =>
          board.patch(item.number, { labels }).catch(() => {}),
      }),
      item.labels.length > 0
        ? m(
            '.labels',
            item.labels.map(label => labelView({ label }))
          )
        : m('.muted', m.t('detail.noLabels'))
    ),

    field(
      m.t('detail.milestone'),
      gear({
        heading: m.t('card.setMilestone'),
        disabled: !canEdit || data.milestones.length === 0,
        multi: false,
        selected: item.milestone ? [item.milestone.number] : [],
        options: data.milestones.map(ms => ({
          value: ms.number,
          label: ms.title,
          view: () => [icon('milestone'), ms.title],
        })),
        onchange: ([number]) =>
          board
            .patch(item.number, { milestone: number ?? null })
            .catch(() => {}),
      }),
      item.milestone
        ? m('.milestone', [icon('milestone'), item.milestone.title])
        : data.milestones.length
          ? m('.muted', m.t('detail.noMilestone'))
          : m('.muted', [
              m.t('detail.noMilestones'),
              m(
                'a',
                { href: `${repo.url}/milestones/new`, target: '_blank' },
                m.t('detail.createMilestone')
              ),
            ])
    ),

    item.pr &&
      field(m.t('detail.pullRequest'), null, [
        m('.branch', [
          icon('git-branch'),
          m('code.branch-name', item.pr.branch),
          '→',
          m('code.branch-name', item.pr.base),
        ]),
        item.state === 'open' && prActionsView({ card: item, labeled: true }),
        canRebase(item, repo) && rebaseButtonView({ card: item }),
        item.state === 'open' &&
          mergeButtonView({ card: item, repo, onmerged: closeDetail }),
        canDeleteBranch(item, repo) && deleteBranchButtonView({ card: item }),
      ]),

    item.state === 'open' &&
      (settings.claudeCli || settings.claudeVscode) &&
      field(
        m.t('claude.title'),
        null,
        claudeActionsView({ card: item, repo: repo.fullName, labeled: true })
      ),

    item.links.length > 0 &&
      field(
        item.type === 'pr' ? m.t('card.closes') : m.t('detail.development'),
        null,
        item.links.map(n =>
          m('a.linked', { href: issueHref(n) }, [
            icon(item.type === 'pr' ? 'issue-opened' : 'git-pull-request'),
            `#${n}`,
          ])
        )
      ),
  ])
}

export const detailView = m.withState(number => {
  const cacheKey = `${board.key}#${number}`.toLowerCase()
  const draftBase = cacheKey // drafts of this issue: ":new", ":body", ":comment:<id>"
  let item = detail.cache.get(cacheKey) || null
  let error = null
  // pull requests: 'conversation', or 'files' at …/changes, so a reload keeps it
  const tab = () =>
    m.route.get?.()?.split('?')[0].endsWith('/changes')
      ? 'files'
      : 'conversation'
  // the tab's URL replaces the current one: Back closes the dialog, not the tab
  const showTab = id =>
    m.route.set(
      `/${board.key}/${number}${id === 'files' ? '/changes' : ''}`,
      filterQuery(),
      { replace: true }
    )

  async function reload() {
    try {
      const fresh = await api('GET', board.url(`/issues/${number}`))
      // keep text that failed to save, so nothing typed gets lost
      if (item?.failed)
        Object.assign(fresh, { body: item.body, bodyHTML: null, failed: true })
      fresh.commentList = fresh.commentList.filter(
        c => !deletedComments.has(c.id)
      )
      fresh.commentList.push(
        ...(item?.commentList.filter(c => c.failed && c.id < 0) || [])
      )
      for (const c of fresh.commentList) {
        const mine = item?.commentList.find(
          old => old.id === c.id && old.failed
        )
        if (mine)
          Object.assign(c, { body: mine.body, bodyHTML: null, failed: true })
      }
      item = fresh
      detail.cache.set(cacheKey, fresh)
      error = null
    } catch (err) {
      error = err.message
    }
    m.redraw()
  }

  // Posts show their raw text right away (bodyHTML null) until settle() reloads GitHub's rendering
  const save = (post, request) => {
    // rendered locally right away; settle() later swaps in GitHub's own HTML
    Object.assign(post, {
      failed: false,
      saving: true,
      bodyHTML: renderMarkdown(post.body),
    })
    request
      .catch(() => (post.failed = true))
      .finally(() => {
        post.saving = false
        m.redraw()
      })
  }
  const saveBody = body => save(item, board.patch(number, { body }))
  // a task list item becomes an issue titled like it; then it is checked off and links the issue
  const taskToIssue = (post, onsave) => async (index, text) => {
    // by its text; markdown in it renders differently, then by position
    const tasks = taskLines(post.body)
    const task =
      tasks.find(x => !x.checked && x.text === text) ||
      (tasks[index]?.checked === false ? tasks[index] : null)
    if (!task) return
    const created = await board.create(
      null,
      text || task.text,
      m.t('task.issueBody', { number })
    )
    if (created) onsave(linkTask(post.body, task, created))
  }
  const saveComment = comment => body => {
    comment.body = body
    save(
      comment,
      board.track(
        number,
        api('PATCH', board.url(`/comments/${comment.id}`), { body })
      )
    )
  }
  // optimistic: gone at once, back with an error message if GitHub refuses
  const deleteComment = comment => {
    const list = item.commentList
    const index = list.indexOf(comment)
    item.commentList = list.filter(c => c !== comment)
    deletedComments.add(comment.id)
    const card = board.card(number)
    if (card) card.comments--
    board
      .track(number, api('DELETE', board.url(`/comments/${comment.id}`)))
      .catch(() => {
        deletedComments.delete(comment.id)
        item.commentList.splice(index, 0, comment)
        if (card) card.comments++
        m.redraw()
      })
  }
  /** Comments and history events merged by time, filtered by the user's choice. */
  const flow = data => {
    const events = data
      ? timelineRows(item.events || [], data.config).map((row, i) => ({
          at: row.at,
          key: `event-${i}-${row.at}`,
          row,
        }))
      : []
    const comments = item.commentList.map(c => ({
      at: c.createdAt,
      comment: c,
    }))
    return [...comments, ...events].sort((a, b) => a.at.localeCompare(b.at))
  }
  const addComment = body => {
    const comment = {
      id: -Date.now(),
      body,
      createdAt: new Date().toISOString(),
      author: auth.user,
    }
    item.commentList.push(comment)
    const card = board.card(number)
    if (card) card.comments++
    save(
      comment,
      board.track(
        number,
        api('POST', board.url(`/issues/${number}/comments`), { body })
      )
    )
  }

  detail.current = {
    number,
    reload,
    get item() {
      return item
    },
  }
  reload()
  function cleanup() {
    if (detail.current?.number === number) detail.current = null
  }

  const tabView = (id, tabIcon, label) =>
    m.m(
      'button.detail-tab',
      {
        role: 'tab',
        'aria-selected': String(tab() === id),
        cls: { selected: tab() === id },
        onclick: () => showTab(id),
      },
      [tabIcon, label]
    )

  return () => {
    const data = board.data
    // handler must not return false: Mithril would preventDefault every mousedown in the dialog
    return m(
      '.overlay',
      {
        onmousedown: evt => {
          if (evt.target === evt.currentTarget) closeDetail()
        },
      },
      [
        m.onRemove(cleanup),
        keyBindingsView('detail', { esc: closeDetail }),
        m('.detail', [
          // head and tabs stay, the body below scrolls
          m('.detail-top', [
            // one line: state, title with number and GitHub link, close
            m('.detail-head', [
              item && stateLabelView({ card: item }),
              item ? titleView({ item }) : m('.spacer'),
              m(
                'button.icon-button.close',
                {
                  onclick: closeDetail,
                  title: m.t('detail.close'),
                  'aria-label': m.t('detail.close'),
                },
                icon('x')
              ),
            ]),
            item?.pr &&
              m('.detail-tabs', { role: 'tablist' }, [
                tabView('conversation', icon('comment'), [
                  m.t('detail.conversation'),
                  m('span.counter', item.commentList.length),
                ]),
                tabView('files', icon('file-diff'), [
                  m.t('detail.filesChanged'),
                  // unknown on boards saved before the count was loaded
                  item.pr.changedFiles != null &&
                    m('span.counter', item.pr.changedFiles),
                ]),
              ]),
          ]),
          m('.detail-body', [
            error && !item && m('.error', error),
            !item && !error && m('.muted.center', m.t('detail.loading')),
            item?.pr &&
              tab() === 'files' &&
              m(
                '.detail-files',
                diffView({ item, repo: data?.repo, onmerged: closeDetail })
              ),
            item &&
              !(item.pr && tab() === 'files') && [
                m('.detail-grid', [
                  m('.detail-main', [
                    m.fragment(
                      { key: 'body' },
                      postView({
                        author: item.author,
                        createdAt: item.createdAt,
                        html: item.bodyHTML,
                        body: item.body,
                        saving: item.saving,
                        failed: item.failed,
                        label: 'detail.opened',
                        draftKey: `${draftBase}:body`,
                        canEdit: item.viewerCanUpdate,
                        onsave: saveBody,
                        ontask: taskToIssue(item, saveBody),
                      })
                    ),
                    // comments and history by time, like GitHub's timeline
                    ...flow(data).map(({ row, comment: c, key }) =>
                      row
                        ? eventRowView({ key, row })
                        : m.fragment(
                            { key: c.id },
                            postView({
                              id: c.id,
                              author: c.author,
                              createdAt: c.createdAt,
                              html: c.bodyHTML,
                              body: c.body,
                              saving: c.saving,
                              failed: c.failed,
                              label: 'detail.commented',
                              draftKey:
                                c.id > 0
                                  ? `${draftBase}:comment:${c.id}`
                                  : null,
                              canEdit: c.viewerCanUpdate || c.failed,
                              ondelete:
                                c.viewerCanDelete && (() => deleteComment(c)),
                              ontask:
                                c.id > 0 && c.viewerCanUpdate
                                  ? taskToIssue(c, saveComment(c))
                                  : null,
                              onsave:
                                c.id < 0
                                  ? text => {
                                      item.commentList =
                                        item.commentList.filter(x => x !== c)
                                      addComment(text)
                                    }
                                  : saveComment(c),
                            })
                          )
                    ),
                    m('.post.new-comment', { key: 'new' }, [
                      m(
                        '.post-avatar',
                        avatarView({ user: auth.user, size: 40 })
                      ),
                      m('.post-box', [
                        m('h3.new-comment-title', m.t('detail.addComment')),
                        editorView({
                          draftKey: `${draftBase}:new`,
                          placeholder: m.t('detail.commentPlaceholder'),
                          saveLabel: m.t('detail.comment'),
                          rows: 5,
                          requireText: true,
                          clearOnSave: true,
                          onsave: addComment,
                          actions: data?.repo.canTriage && stateButton(item),
                        }),
                      ]),
                    ]),
                  ]),
                  data && sidebarView({ item, data }),
                ]),
              ],
          ]),
        ]),
      ]
    )
  }
})
